import { z } from "zod";
import {
    EntityCloseStage,
    EntityCloseStatus,
    EntityProfileType,
} from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import {
    BillingTransferRequestStatus,
    BillingTransferRequestType,
} from "@common/constants/domains/billing/billingTransferRequest";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { OrganizationEntityPriority } from "@common/types/organizationEntity";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { WorkspaceSortValue } from "@common/types/views/workspaceViews";
import { WhoPaysOption } from "@common/types/filters/clients";

const inviteSchema = z.object({
    id: z.number(),
    role: z.nativeEnum(UserWorkspaceRole),
    email: z.string(),
    iconColor: z.string(),
});

const memberSchema = z.object({
    userId: z.number(),
    role: z.nativeEnum(UserWorkspaceRole),
    email: z.string(),
    iconColor: z.string(),
    avatar: z.string().nullable(),
    preferredName: z.string().nullable(),
    organizationId: z.string().nullable(),
});

const clientEntitySchema = z.object({
    id: z.coerce.number(),
    name: z.string(),
    bookkeepingStartDate: z.string(),
    type: z.string(),
    iconColor: z.string(),
    logo: z.string().nullable(),
    isBusiness: z.boolean(),
    closeStatus: z.nativeEnum(EntityCloseStatus).nullable().optional(),
    legalType: z.nativeEnum(EntityProfileType).nullable(),
    closeDate: z.string().date().nullable().optional(),
    dueDate: z.string().date().nullable(),
    priority: z.nativeEnum(OrganizationEntityPriority).nullable().optional(),
    services: z
        .array(z.nativeEnum(OrganizationServicesOffered))
        .nullable()
        .optional(),
    note: z.string().nullable().optional(),
    invites: z.array(inviteSchema),
    members: z.array(memberSchema),
    closeStage: z.nativeEnum(EntityCloseStage).nullable().optional(),
    closeStageUpdatedAt: z.string().date().nullable().optional(),
    team: z.array(z.object({})).optional(),
    workspaceId: z.string(),
});

export const pendingTransferRequestSchema = z.object({
    id: z.string(),
    type: z.nativeEnum(BillingTransferRequestType),
    organizationId: z.string(),
});

export const clientOrgBillingTransferRequestSchema = z.object({
    id: z.string(),
    status: z.enum([
        BillingTransferRequestStatus.PENDING,
        BillingTransferRequestStatus.DECLINED,
    ]),
    recipientName: z.string().nullable(),
    recipientEmail: z.string().nullable(),
    declineReason: z.string().nullable(),
    createdAt: z.coerce.date(),
    resolvedAt: z.coerce.date().nullable(),
    resolvedByName: z.string().nullable(),
});

export const clientSchema = z.object({
    id: z.string(),
    name: z.string(),
    note: z.string().nullable(),
    plan: z.nativeEnum(FLAT_RATE_PLANS).nullable(),
    bookkeepingStartDate: z.string(),
    isFirmWorkspace: z.boolean(),
    /**
     * @deprecated Mirrors `isFirmWorkspace` for browser bundles loaded before
     * the rename. Remove together with the `isOrganizationDemo` column.
     */
    isOrganizationDemo: z.boolean().optional(),
    isDemo: z.boolean(),
    invites: z.array(inviteSchema).optional(), // computed on FE
    members: z.array(memberSchema).optional(), // computed on FE
    team: z.array(z.object({})).optional(), // computed on FE
    services: z.array(z.nativeEnum(OrganizationServicesOffered)).nullable(),
    entities: z.array(clientEntitySchema),
    isOrganizationBillingOwner: z.boolean(),
    hasUsersToTransferBillingTo: z.boolean(),
    primaryAdminUserId: z.number().nullable(),

    pendingTransferRequests: z.array(pendingTransferRequestSchema),
    orgBillingTransferRequest: clientOrgBillingTransferRequestSchema.nullable(),

    subscriptionStartDate: z.string().nullable().optional(),
    primaryAdminUserEmail: z.string().nullable().optional(),
    assigneeId: z.number().nullable().optional(),
    tasksCount: z.number().nullable().optional(),
    doneTasksCount: z.number().nullable().optional(),
    unreadTasksCount: z.number().nullable().optional(),
});

export type InviteSchema = z.infer<typeof inviteSchema>;
export type MemberSchema = z.infer<typeof memberSchema>;
export type ClientEntitySchema = z.infer<typeof clientEntitySchema>;
export type ClientSchema = z.infer<typeof clientSchema>;
export type PendingTransferRequestSchema = z.infer<
    typeof pendingTransferRequestSchema
>;
export type ClientOrgBillingTransferRequest = z.infer<
    typeof clientOrgBillingTransferRequestSchema
>;

// Zod schemas for enums
const flatRatePlansSchema = z.nativeEnum(FLAT_RATE_PLANS);
const organizationServicesOfferedSchema = z.nativeEnum(
    OrganizationServicesOffered,
);
const workspaceSortValueSchema = z.nativeEnum(WorkspaceSortValue);
const whoPaysOptionSchema = z.nativeEnum(WhoPaysOption);

// Filters schema
export const workspaceFiltersSchema = z.object({
    search: z.string().optional(),
    plan: flatRatePlansSchema.optional(),
});

// Complete query schema
export const getWorkspacesQuerySchema = z.object({
    filters: workspaceFiltersSchema.optional(),
    sort: workspaceSortValueSchema.optional(),
    page: z.coerce.number().optional(),
    limit: z.coerce.number().optional(),
    organizationId: z.string().uuid().optional(),
    excludeUserOrganization: z.boolean().optional(),
    workspaceId: z.string().uuid().optional(),
});

export const clientsFiltersSchema = z.object({
    search: z.string().optional(),
    plan: flatRatePlansSchema.optional(),
    plans: z.array(flatRatePlansSchema).optional(),
    legalTypes: z.array(z.nativeEnum(EntityProfileType)).optional(),
    assigneeIds: z.array(z.coerce.number()).optional(),
    teamMemberIds: z.array(z.coerce.number()).optional(),
    services: z.array(organizationServicesOfferedSchema).optional(),
    whoPays: z.array(whoPaysOptionSchema).optional(),
    dueDateStart: z.coerce.date().optional(),
    dueDateEnd: z.coerce.date().optional(),
    bookkeepingStartDateStart: z.coerce.date().optional(),
    bookkeepingStartDateEnd: z.coerce.date().optional(),
    closeStatus: z.array(z.nativeEnum(EntityCloseStatus)).optional(),
    closeDateStart: z.coerce.date().optional(),
    closeDateEnd: z.coerce.date().optional(),
    closeStage: z.array(z.nativeEnum(EntityCloseStage)).optional(),
    priority: z.array(z.nativeEnum(OrganizationEntityPriority)).optional(),
    hasUnread: z.coerce.boolean().optional(),
});

// Complete query schema
export const clientsQuerySchema = z.object({
    filters: clientsFiltersSchema.optional(),
    sort: workspaceSortValueSchema.optional(),
    page: z.coerce.number().optional(),
    limit: z.coerce.number().optional(),
    organizationId: z.string().uuid(),
});

export type GetWorkspacesQueryParams = z.infer<typeof getWorkspacesQuerySchema>;
export type WorkspaceFilters = z.infer<typeof workspaceFiltersSchema>;

export type ClientsQueryParams = z.infer<typeof clientsQuerySchema>;
export type ClientsFilters = z.infer<typeof clientsFiltersSchema>;

export const clientsResponseSchema = z.object({
    clients: z.array(clientSchema),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
});
export type ClientsResponseSchema = z.infer<typeof clientsResponseSchema>;
